import { z } from "zod";
import {
  DELEGATE_TOOL_NAME,
  WEB_SEARCH_CAPABILITY,
  buildDelegateToolDefinition,
  DelegateInputSchema,
  REMEMBER_PREFERENCE_TOOL_NAME,
  RememberPreferenceInputSchema,
  delegateToolDefinition,
  rememberPreferenceToolDefinition,
} from "./index.ts";

/**
 * The tools an avatar can be given.
 *
 * Previously the two tools were hardcoded into the session config, which meant
 * every avatar got exactly the same ones. A marketplace avatar needs tools
 * specific to its job, so which tools exist and which an avatar has must be
 * data rather than a literal array.
 *
 * Each entry carries three things that used to live in three different files:
 * the schema OpenAI is given, the schema used to validate what comes back, and
 * the server route that executes it.
 */
const DOCS = {
  plane: "https://developers.plane.so/api-reference/introduction",
  github: "https://docs.github.com/en/rest",
  webSearch: "https://platform.openai.com/docs/guides/tools-web-search",
} as const;

export type RegisteredTool = {
  id: string;
  /** Function schema sent to the Realtime session. */
  definition: Record<string, unknown>;
  /** Validates the arguments the model produces. */
  argsSchema: z.ZodTypeAny;
  /** Server route the browser posts the call to. */
  endpoint: string;
  /** Shown in the marketplace so a user can see what an avatar can do. */
  summary: string;
  /** One line telling the model when to reach for it. Rendered into the charter. */
  usage?: string;
  /** Live documentation for the underlying service, shown to model and user. */
  docsUrl?: string;
  /**
   * A capability that changes another tool rather than adding one of its own.
   * Listed in the charter and the marketplace, but sent no function schema.
   */
  virtual?: boolean;
};

export const TOOL_REGISTRY: Record<string, RegisteredTool> = {
  [DELEGATE_TOOL_NAME]: {
    id: DELEGATE_TOOL_NAME,
    definition: delegateToolDefinition,
    argsSchema: DelegateInputSchema,
    endpoint: "/api/delegate",
    summary: "Persistent memory, task state, permissions and live web lookups",
    usage:
      "recall what the user told you before, read or change task state, check whether " +
      "something is permitted, or look up live information from the web",
  },
  [REMEMBER_PREFERENCE_TOOL_NAME]: {
    id: REMEMBER_PREFERENCE_TOOL_NAME,
    definition: rememberPreferenceToolDefinition,
    argsSchema: RememberPreferenceInputSchema,
    endpoint: "/api/preference",
    summary: "Remembers how you want to be spoken to, and durable facts about you",
    usage:
      "record a lasting preference about how you should speak, or a durable fact the user " +
      "stated about themselves",
  },
  ...planeTools(),
  ...githubTools(),
  [WEB_SEARCH_CAPABILITY]: {
    id: WEB_SEARCH_CAPABILITY,
    definition: {},
    argsSchema: z.any(),
    endpoint: "/api/delegate",
    virtual: true,
    summary: "Live information from the public web",
    usage:
      "answer anything that changes in the world and is not about this user - weather, news, " +
      "prices, opening hours, sports results",
    docsUrl: DOCS.webSearch,
  },
};

/** What every avatar gets unless it says otherwise. Matches previous behaviour. */
/** Project-management tools. The registry is the only place they are described. */
function planeTools(): Record<string, RegisteredTool> {
  const common = { endpoint: "", docsUrl: DOCS.plane };
  const priority = { type: "string", enum: ["urgent", "high", "medium", "low", "none"] };
  const status = { type: "string", description: 'Status to set, e.g. "Done", "Cancelled", "In Progress", "Todo", "Backlog"' };
  const PRIORITY = z.enum(["urgent", "high", "medium", "low", "none"]);
  return {
    plane_list_projects: {
      ...common,
      id: "plane_list_projects",
      definition: fn("plane_list_projects", "List the projects in the workspace.", {}),
      argsSchema: z.object({}),
      summary: "Lists the projects in the workspace",
      usage: "see what projects exist before reading or creating work",
    },
    plane_create_project: {
      ...common,
      id: "plane_create_project",
      definition: fn("plane_create_project", "Create a new project in the workspace.", {
        name: { type: "string", description: "Project name" },
        description: { type: "string", description: "What the project is for" },
      }, ["name"]),
      argsSchema: z.object({ name: z.string().min(1), description: z.string().optional() }),
      summary: "Creates a project",
      usage: "start a new project once the user has agreed its name",
    },
    plane_update_project: {
      ...common,
      id: "plane_update_project",
      definition: fn("plane_update_project", "Rename a project or change its description.", {
        projectId: { type: "string", description: "Project NAME (preferred) or id" },
        name: { type: "string" },
        description: { type: "string" },
      }, ["projectId"]),
      argsSchema: z.object({
        projectId: z.string().min(1),
        name: z.string().optional(),
        description: z.string().optional(),
      }),
      summary: "Renames a project or changes its description",
      usage: "apply a change to a project the user has agreed to",
    },
    plane_delete_project: {
      ...common,
      id: "plane_delete_project",
      definition: fn("plane_delete_project",
        "Permanently delete a project and every work item in it. This cannot be undone - only call it when the user has clearly asked for this specific project to be deleted.", {
        projectId: { type: "string", description: "Project NAME (preferred) or id" },
      }, ["projectId"]),
      argsSchema: z.object({ projectId: z.string().min(1) }),
      summary: "Deletes a project permanently",
      usage: "remove a project the user has explicitly and unambiguously asked you to delete",
    },
    plane_list_issues: {
      ...common,
      id: "plane_list_issues",
      definition: fn("plane_list_issues", "List work items in a project, with their status and priority.", {
        projectId: { type: "string", description: "Project NAME (preferred) or id" },
        status: { type: "string", description: 'Optional filter, e.g. "Done" or "In Progress"' },
      }, ["projectId"]),
      argsSchema: z.object({ projectId: z.string().min(1), status: z.string().optional() }),
      summary: "Reads work items, their status and priority",
      usage: "answer what is outstanding, in progress, done, or highest priority",
    },
    plane_create_issue: {
      ...common,
      id: "plane_create_issue",
      definition: fn("plane_create_issue", "Create a work item in a project.", {
        projectId: { type: "string", description: "Project NAME (preferred) or id" },
        name: { type: "string", description: "Short title of the work item" },
        description: { type: "string" },
        priority,
        status,
        labels: {
          type: "array",
          items: { type: "string" },
          description: "Labels to attach, by name. Any that do not exist are created.",
        },
        parent: { type: "string", description: "Title or id of the item this belongs under" },
      }, ["projectId", "name"]),
      argsSchema: z.object({
        projectId: z.string().min(1),
        name: z.string().min(1),
        description: z.string().optional(),
        priority: PRIORITY.optional(),
        status: z.string().optional(),
        labels: z.array(z.string()).optional(),
        parent: z.string().optional(),
      }),
      summary: "Creates a work item, optionally labelled and under a parent",
      usage: "record a task the user has agreed to add",
    },
    plane_update_issue: {
      ...common,
      id: "plane_update_issue",
      definition: fn("plane_update_issue",
        "Change a work item: its status (for example mark it Done or Cancelled), title, description or priority.", {
        projectId: { type: "string", description: "Project NAME (preferred) or id" },
        issueId: { type: "string", description: "Work item TITLE (preferred) or id" },
        name: { type: "string" },
        description: { type: "string" },
        priority,
        status,
        labels: {
          type: "array",
          items: { type: "string" },
          description: "Replaces the item's labels, by name. Any that do not exist are created.",
        },
        parent: { type: "string", description: "Title or id to file this under; empty string detaches it" },
      }, ["projectId", "issueId"]),
      argsSchema: z.object({
        projectId: z.string().min(1),
        issueId: z.string().min(1),
        name: z.string().optional(),
        description: z.string().optional(),
        priority: PRIORITY.optional(),
        status: z.string().optional(),
        labels: z.array(z.string()).optional(),
        parent: z.string().optional(),
      }),
      summary: "Changes a work item's status, title, description, priority, labels or parent",
      usage: "mark something done or cancelled, group items under a label or a parent, or apply a change the user agreed to",
    },
    plane_delete_issue: {
      ...common,
      id: "plane_delete_issue",
      definition: fn("plane_delete_issue",
        "Permanently delete a work item. This cannot be undone - prefer setting its status to Cancelled unless the user asked for deletion.", {
        projectId: { type: "string", description: "Project NAME (preferred) or id" },
        issueId: { type: "string", description: "Work item TITLE (preferred) or id" },
      }, ["projectId", "issueId"]),
      argsSchema: z.object({ projectId: z.string().min(1), issueId: z.string().min(1) }),
      summary: "Deletes a work item permanently",
      usage: "remove a work item the user explicitly asked you to delete",
    },
    plane_grant_viewer_access: {
      ...common,
      id: "plane_grant_viewer_access",
      definition: fn("plane_grant_viewer_access",
        "Create a read-only login so a person can watch specific projects without being able to change them. You MUST know which projects before calling this - ask the user if they have not said. Returns the address, username and a one-time password to read out.", {
        email: { type: "string", description: "Email address, which becomes their username" },
        projects: {
          type: "array",
          items: { type: "string" },
          description: "Names or ids of the projects this person may see. Ask the user; never guess.",
        },
        displayName: { type: "string", description: "How to show their name" },
      }, ["email", "projects"]),
      argsSchema: z.object({
        email: z.string().min(3),
        // Deliberately NOT .min(1): an empty list must reach the handler so it
        // can ask which project, rather than being rejected as a malformed call
        // and making the avatar say "please rephrase".
        projects: z.array(z.string()),
        displayName: z.string().optional(),
      }),
      summary: "Creates a read-only login scoped to chosen projects",
      usage: "give someone view access, once the user has said which projects they may see",
    },
    plane_share_access: {
      ...common,
      id: "plane_share_access",
      definition: fn("plane_share_access",
        "Get the link to the project workspace so you can share it with the user. Call this whenever the user asks where the work lives, for the link, or for access.", {}),
      argsSchema: z.object({}),
      summary: "Gives you the workspace link to share with the user",
      usage: "answer 'where can I see this', 'what is the link', or any request for access",
    },
  };
}

/** Read-heavy GitHub tools. Nothing here pushes code. */
function githubTools(): Record<string, RegisteredTool> {
  const common = { endpoint: "", docsUrl: DOCS.github };
  return {
    github_list_issues: {
      ...common,
      id: "github_list_issues",
      definition: fn("github_list_issues", "List issues in a repository.", {
        repo: { type: "string", description: 'owner/name, e.g. "acme/web"' },
        state: { type: "string", enum: ["open", "closed", "all"] },
      }),
      argsSchema: z.object({
        repo: z.string().optional(),
        state: z.enum(["open", "closed", "all"]).optional(),
      }),
      summary: "Reads issues in a repository",
      usage: "see what is already reported before raising something new",
    },
    github_create_issue: {
      ...common,
      id: "github_create_issue",
      definition: fn("github_create_issue", "File an issue in a repository.", {
        repo: { type: "string" },
        title: { type: "string", description: "One-line summary of the defect" },
        body: { type: "string", description: "Steps, what happened, what should have happened" },
        labels: { type: "array", items: { type: "string" } },
      }, ["title"]),
      argsSchema: z.object({
        repo: z.string().optional(),
        title: z.string().min(1),
        body: z.string().optional(),
        labels: z.array(z.string()).optional(),
      }),
      summary: "Files an issue",
      usage: "report a defect you have actually observed, never one you suspect",
    },
    github_read_file: {
      ...common,
      id: "github_read_file",
      definition: fn("github_read_file", "Read a file from a repository.", {
        repo: { type: "string" },
        path: { type: "string", description: "Path within the repository" },
      }, ["path"]),
      argsSchema: z.object({ repo: z.string().optional(), path: z.string().min(1) }),
      summary: "Reads a file from a repository",
      usage: "look at the actual code before reasoning about it",
    },
  };
}

function fn(name: string, description: string, props: Record<string, unknown>, required: string[] = []) {
  return {
    type: "function" as const,
    name,
    description,
    parameters: { type: "object", properties: props, required, additionalProperties: false },
  };
}

export const DEFAULT_TOOL_IDS: readonly string[] = [
  DELEGATE_TOOL_NAME,
  REMEMBER_PREFERENCE_TOOL_NAME,
  WEB_SEARCH_CAPABILITY,
];

export const isKnownToolId = (id: unknown): id is string =>
  typeof id === "string" && id in TOOL_REGISTRY;

/**
 * Function schemas for a set of tool ids, in registry order so the payload is
 * stable between sessions. Unknown ids are dropped rather than throwing: a
 * stored avatar must keep working after a tool is removed from the build.
 */
export function toolDefinitionsFor(ids: readonly string[] = DEFAULT_TOOL_IDS) {
  const webSearch = ids.includes(WEB_SEARCH_CAPABILITY);
  return ids
    .filter(isKnownToolId)
    .filter((id) => !TOOL_REGISTRY[id].virtual)
    .map((id) =>
      id === DELEGATE_TOOL_NAME
        ? buildDelegateToolDefinition({ webSearch })
        : TOOL_REGISTRY[id].definition,
    );
}

export const hasWebSearch = (ids: readonly string[] = DEFAULT_TOOL_IDS) =>
  ids.includes(WEB_SEARCH_CAPABILITY);

/** Argument validators keyed by tool name, for the function-call loop. */
export function validatorsFor(ids: readonly string[] = DEFAULT_TOOL_IDS) {
  const out: Record<string, z.ZodTypeAny> = {};
  for (const id of ids) {
    if (isKnownToolId(id)) out[id] = TOOL_REGISTRY[id].argsSchema;
  }
  return out;
}

/**
 * Where the browser posts a call for this tool.
 *
 * A registered tool without its own endpoint goes to the generic runner, so a
 * new job-specific tool needs a handler but no route and no client change.
 */
export function endpointFor(toolName: string): string {
  const t = TOOL_REGISTRY[toolName];
  if (!t) return TOOL_REGISTRY[DELEGATE_TOOL_NAME].endpoint;
  return t.endpoint || `/api/tool/${toolName}`;
}

/** Unknown ids, for reporting a bad capability list back to the caller. */
export function unknownToolIds(ids: readonly string[]): string[] {
  return ids.filter((id) => !isKnownToolId(id));
}
